import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Swal from "sweetalert2";

import {
  updatePartnerConfig,
  getPartnerConfigById,
} from "../../../services/partnerConfigurations/partnerConfigurations";

function AddEditPartnerConfiguration() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm();

  const navigate = useNavigate();

  const { id } = useParams();

  const fetchPartnerConfigById = async () => {
    const partnerConfigRes = await getPartnerConfigById(id);
    const { success, error } = partnerConfigRes || {};
    const data = partnerConfigRes;
    if (success) {
      if (data) {
        reset({
          ...data,
          active: data.active === true ? "Yes" : "No",
          payoutProcessingEnabled:
            data.payoutProcessingEnabled === true ? "Yes" : "No",
        });
        return;
      }
    }
    Swal.fire({
      title: "Oops",
      text: error?.message || "Something went wrong",
      icon: "warning",
      confirmButtonText: "Ok",
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/app/partner-config");
      }
    });
  };

  useEffect(() => {
    fetchPartnerConfigById();
  }, [id]);

  const handleDailyWireLimitChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setValue("dailyWireLimit", value, { shouldValidate: true });
  };

  const handlePriorityChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setValue("priority", value, { shouldValidate: true });
  };

  const handlePayoutAmountHoldChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    const normalizedValue = value.replace(/(\..*)\./g, "$1");
    setValue("payoutAmountHold", normalizedValue, { shouldValidate: true });
  };

  const updateConfig = async (data) => {
    const activeValue =
      data.active === "Yes" || data.active === true ? true : false;
    const payoutProcessingEnabledValue =
      data.payoutProcessingEnabled === "Yes" ||
      data.payoutProcessingEnabled === true
        ? true
        : false;
    console.log(data);
    const updateDataRes = await updatePartnerConfig({
      ...data,
      active: activeValue,
      payoutProcessingEnabled: payoutProcessingEnabledValue,
    });
    const { success, error } = updateDataRes || {};
    if (success) {
      Swal.fire({
        title: "Success",
        text: "Partner configuration updated successfully",
        icon: "success",
        confirmButtonText: "Ok",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/app/partner-config");
        }
      });
      return;
    }
    Swal.fire({
      title: "Oops",
      text: error?.message || "Something went wrong",
      icon: "warning",
      confirmButtonText: "Ok",
    });
  };

  return (
    <>
      <Box sx={{ ml: "4", pt: "14 !important", pb: "5", px: 3 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "25px",
            marginTop: "20px",
            gap: "16px",
          }}
        >
          <Link to="/app/partner-config">
            <ArrowBackIcon
              fontSize="large"
              color="secondary"
              sx={{ background: "#040721", p: 1, borderRadius: "50%" }}
            />
          </Link>
          <Typography
            variant="h5"
            component="h5"
            fontWeight={"bold"}
            color={"#3d3d3d"}
          >
            Edit Partner Configuration
          </Typography>
        </div>
        <Box sx={{ background: "#ffffff", p: 2, mb: 4, pb: 4 }}>
          <form onSubmit={handleSubmit(updateConfig)}>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="partner"
                  >
                    Partner Name: <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("partner", { required: true })}
                    className={`form-control ${
                      errors.partner && "invalid-form-input"
                    }`}
                    type="text"
                    id="partner"
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="active"
                  >
                    Active: <small className="text-danger fs-6">*</small>
                  </label>
                  <select
                    {...register("active", { required: true })}
                    className={`form-control ${
                      errors.active && "invalid-form-input"
                    }`}
                    id="active"
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="priority"
                  >
                    Priority: <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("priority", {
                      required: true,
                      pattern: {
                        value: /^[0-9]*$/,
                        message: "Only numbers are allowed",
                      },
                    })}
                    className={`form-control ${
                      errors.priority && "invalid-form-input"
                    }`}
                    type="text"
                    id="priority"
                    inputMode="numeric"
                    onChange={handlePriorityChange}
                  />
                  {errors.priority && (
                    <span className="text-danger">
                      {errors.priority.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="payoutAmountHold"
                  >
                    Payout Amount Hold:{" "}
                    <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("payoutAmountHold", {
                      required: true,
                      pattern: {
                        value: /^\d*\.?\d*$/,
                        message: "Only numbers and decimals are allowed",
                      },
                    })}
                    className={`form-control ${
                      errors.payoutAmountHold && "invalid-form-input"
                    }`}
                    type="text"
                    id="payoutAmountHold"
                    inputMode="decimal"
                    onChange={handlePayoutAmountHoldChange}
                  />
                  {errors.payoutAmountHold && (
                    <span className="text-danger">
                      {errors.payoutAmountHold.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="dailyWireLimit"
                  >
                    Partner Limit: <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("dailyWireLimit", {
                      required: true,
                      pattern: {
                        value: /^[0-9]*$/,
                        message: "Only numbers are allowed",
                      },
                    })}
                    className={`form-control ${
                      errors.dailyWireLimit && "invalid-form-input"
                    }`}
                    type="text"
                    id="dailyWireLimit"
                    inputMode="numeric"
                    onChange={handleDailyWireLimitChange}
                  />
                  {errors.dailyWireLimit && (
                    <span className="text-danger">
                      {errors.dailyWireLimit.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label fw-bold text-secondary user-select-none d-block">
                    Payout Processing Enabled:{" "}
                    <small className="text-danger fs-6">*</small>
                  </label>
                  <div className="d-flex gap-4 mt-2">
                    <div className="form-check">
                      <input
                        {...register("payoutProcessingEnabled", {
                          required: true,
                        })}
                        className={`form-check-input ${
                          errors.payoutProcessingEnabled &&
                          "invalid-form-input"
                        }`}
                        type="radio"
                        id="payoutProcessingEnabledYes"
                        value="Yes"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="payoutProcessingEnabledYes"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        {...register("payoutProcessingEnabled", {
                          required: true,
                        })}
                        className={`form-check-input ${
                          errors.payoutProcessingEnabled &&
                          "invalid-form-input"
                        }`}
                        type="radio"
                        id="payoutProcessingEnabledNo"
                        value="No"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="payoutProcessingEnabledNo"
                      >
                        No
                      </label>
                    </div>
                  </div>
                  {errors.payoutProcessingEnabled && (
                    <span className="text-danger">
                      This field is required
                    </span>
                  )}
                </div>
              </div>
            </div>
            <hr style={{ height: "0.5px", marginTop: "32px" }} />
            <div>
              <button
                type="submit"
                className="btn btn-primary py-2 px-5"
                name="save"
                variant="contained"
                sx={{ width: "25%" }}
              >
                Update
              </button>
            </div>
          </form>
        </Box>
      </Box>
    </>
  );
}

export default AddEditPartnerConfiguration;
